/**
 * CognitiveDNAPanel — The Replica's "Brain" Dashboard
 * 
 * Shows how well the Replica knows its human owner through:
 * - Overall DNA score (0–100) with animated ring
 * - 6 competency metrics with progress bars
 * - 3 learning channels (Shadow / Correct / Debrief) with counters
 * - Time saved display
 * - Data ownership banner
 * 
 * YOUR data. YOUR cognitive digital twin.
 */

import { useState, useEffect } from 'react';
import {
    Brain,
    Eye,
    PenLine,
    MessageCircle,
    Clock,
    Shield,
    TrendingUp,
    Fingerprint,
    Sparkles,
    Target,
    Zap,
    BookOpen,
} from 'lucide-react';
import { api } from '../../lib/api';

interface CognitiveDNA {
    id: string;
    replicaId: string;
    correctionRate: number;
    acceptanceRate: number;
    styleMatchScore: number;
    judgmentScore: number;
    escalationAccuracy: number;
    domainExpertise: number;
    totalObservations: number;
    totalCorrections: number;
    totalDebriefs: number;
    totalApprovals: number;
    totalInteractions: number;
    estimatedTimeSaved: number;
    overallScore: number;
}

interface ProgressData {
    channelBreakdown: {
        shadow: number;
        correct: number;
        debrief: number;
    };
    dailyActivity: { date: string; count: number }[];
    timeSaved: {
        seconds: number;
        minutes: number;
        hours: number;
        label: string;
    };
    recentEventsCount: number;
}

interface CognitiveDNAPanelProps {
    replicaId: string;
    replicaName?: string;
    compact?: boolean;
}

export default function CognitiveDNAPanel({ replicaId, replicaName, compact = false }: CognitiveDNAPanelProps) {
    const [dna, setDna] = useState<CognitiveDNA | null>(null);
    const [progress, setProgress] = useState<ProgressData | null>(null);
    const [loading, setLoading] = useState(true);
    const [animatedScore, setAnimatedScore] = useState(0);

    useEffect(() => {
        loadData();
    }, [replicaId]);

    // Animate the score ring
    useEffect(() => {
        if (!dna) return;
        const target = dna.overallScore;
        const duration = 1500;
        const startTime = Date.now();
        const startVal = animatedScore;

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const t = Math.min(elapsed / duration, 1);
            // Ease-out cubic
            const eased = 1 - Math.pow(1 - t, 3);
            setAnimatedScore(startVal + (target - startVal) * eased);
            if (t < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
    }, [dna?.overallScore]);

    async function loadData() {
        setLoading(true);
        try {
            const [dnaRes, progressRes] = await Promise.all([
                api.get<any>(`/cognitive/${replicaId}/dna`),
                api.get<any>(`/cognitive/${replicaId}/progress`),
            ]);

            if (dnaRes.data?.dna) setDna(dnaRes.data.dna);
            if (progressRes.data?.progress) setProgress(progressRes.data.progress);
        } catch (err) {
            console.error('Failed to load cognitive DNA:', err);
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingPulse}>
                    <Brain size={32} style={{ opacity: 0.3 }} />
                    <span style={{ opacity: 0.5, marginTop: 8 }}>Loading cognitive twin data...</span>
                </div>
            </div>
        );
    }

    // Use default data when API hasn't returned anything yet
    const safeDna: CognitiveDNA = dna || {
        id: '', replicaId: replicaId,
        correctionRate: 0, acceptanceRate: 0, styleMatchScore: 0,
        judgmentScore: 0, escalationAccuracy: 0, domainExpertise: 0,
        totalObservations: 0, totalCorrections: 0, totalDebriefs: 0,
        totalApprovals: 0, totalInteractions: 0, estimatedTimeSaved: 0,
        overallScore: 0,
    };

    const scoreColor = getScoreColor(safeDna.overallScore);

    // Compact mode for dashboard cards
    if (compact) {
        return (
            <div style={styles.compactContainer}>
                <div style={styles.compactHeader}>
                    <Brain size={20} color="var(--accent)" />
                    <span style={styles.compactTitle}>Cognitive Twin</span>
                </div>
                <div style={styles.compactBody}>
                    <div style={styles.scoreRingCompact}>
                        <svg width="60" height="60" viewBox="0 0 60 60">
                            <circle cx="30" cy="30" r="24" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
                            <circle
                                cx="30" cy="30" r="24"
                                fill="none" stroke={scoreColor} strokeWidth="4"
                                strokeDasharray={`${(animatedScore / 100) * 150.8} 150.8`}
                                strokeLinecap="round"
                                transform="rotate(-90 30 30)"
                                style={{ transition: 'stroke-dasharray 0.3s ease' }}
                            />
                        </svg>
                        <span style={{ ...styles.scoreText, fontSize: 14, color: scoreColor }}>
                            {Math.round(animatedScore)}
                        </span>
                    </div>
                    <div style={styles.compactStats}>
                        <div style={styles.compactStat}>
                            <span style={styles.compactStatValue}>{safeDna.totalInteractions}</span>
                            <span style={styles.compactStatLabel}>signals</span>
                        </div>
                        <div style={styles.compactStat}>
                            <span style={styles.compactStatValue}>{Math.round(safeDna.acceptanceRate)}%</span>
                            <span style={styles.compactStatLabel}>acceptance</span>
                        </div>
                        <div style={styles.compactStat}>
                            <span style={styles.compactStatValue}>
                                {progress?.timeSaved.label || '0min'}
                            </span>
                            <span style={styles.compactStatLabel}>saved</span>
                        </div>
                    </div>
                </div>

                {/* Shadow → Correct → Debrief Learning Channels */}
                <div style={styles.compactChannels}>
                    <div style={styles.compactChannel}>
                        <Eye size={14} color="rgba(255,255,255,0.4)" />
                        <span style={{ ...styles.compactChannelCount, color: 'var(--accent)' }}>{safeDna.totalObservations}</span>
                        <span style={styles.compactChannelLabel}>Shadow</span>
                    </div>
                    <div style={styles.compactChannelArrow}>→</div>
                    <div style={styles.compactChannel}>
                        <PenLine size={14} color="rgba(255,255,255,0.4)" />
                        <span style={{ ...styles.compactChannelCount, color: 'var(--accent)' }}>{safeDna.totalCorrections}</span>
                        <span style={styles.compactChannelLabel}>Correct</span>
                    </div>
                    <div style={styles.compactChannelArrow}>→</div>
                    <div style={styles.compactChannel}>
                        <MessageCircle size={14} color="rgba(255,255,255,0.4)" />
                        <span style={{ ...styles.compactChannelCount, color: 'var(--accent)' }}>{safeDna.totalDebriefs}</span>
                        <span style={styles.compactChannelLabel}>Debrief</span>
                    </div>
                </div>
            </div>
        );
    }

    // Full view
    return (
        <div style={styles.container}>
            {/* Data Ownership Banner */}
            <div style={styles.ownershipBanner}>
                <Fingerprint size={16} color="rgba(255,255,255,0.4)" />
                <span>
                    <strong>Your data, your twin.</strong> {replicaName || 'Your Replica'} learns exclusively from YOUR interactions.
                    This data belongs to you and is never shared.
                </span>
            </div>

            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <Brain size={28} color="var(--accent)" />
                    <div>
                        <h3 style={styles.title}>Cognitive DNA</h3>
                        <p style={styles.subtitle}>
                            How well {replicaName || 'your Replica'} knows you
                        </p>
                    </div>
                </div>
                <div style={styles.headerRight}>
                    <div style={{ ...styles.timeSavedBadge, background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.2)', color: 'var(--accent)' }}>
                        <Clock size={14} color="var(--accent)" />
                        <span>{progress?.timeSaved.label || '0 min saved'}</span>
                    </div>
                </div>
            </div>

            {/* Score Ring + Channels Grid */}
            <div style={styles.mainGrid}>
                {/* Overall Score Ring */}
                <div style={styles.scoreSection}>
                    <div style={styles.scoreRing}>
                        <svg width="140" height="140" viewBox="0 0 140 140">
                            {/* Background ring */}
                            <circle cx="70" cy="70" r="58" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
                            {/* Score ring */}
                            <circle
                                cx="70" cy="70" r="58"
                                fill="none" stroke={scoreColor} strokeWidth="8"
                                strokeDasharray={`${(animatedScore / 100) * 364.4} 364.4`}
                                strokeLinecap="round"
                                transform="rotate(-90 70 70)"
                            />
                            {/* Glow effect */}
                            <circle
                                cx="70" cy="70" r="58"
                                fill="none" stroke={scoreColor} strokeWidth="2"
                                strokeDasharray={`${(animatedScore / 100) * 364.4} 364.4`}
                                strokeLinecap="round"
                                transform="rotate(-90 70 70)"
                                opacity="0.3"
                                filter="blur(4px)"
                            />
                        </svg>
                        <div style={styles.scoreCenter}>
                            <span style={{ ...styles.scoreBig, color: scoreColor }}>
                                {Math.round(animatedScore)}
                            </span>
                            <span style={styles.scoreLabel}>Overall</span>
                        </div>
                    </div>
                    <p style={styles.scoreDescription}>
                        {getScoreDescription(safeDna.overallScore)}
                    </p>
                </div>

                {/* Learning Channels */}
                <div style={styles.channelsSection}>
                    <h4 style={styles.sectionTitle}>
                        <Sparkles size={16} color="#fbbf24" />
                        Learning Channels
                    </h4>
                    <div style={styles.channelGrid}>
                        <ChannelCard
                            icon={<Eye size={20} />}
                            label="Shadow"
                            description="Observations of your work"
                            count={safeDna.totalObservations}
                            color="var(--accent)" /* brand accent */
                        />
                        <ChannelCard
                            icon={<PenLine size={20} />}
                            label="Correct"
                            description="Your red-pen corrections"
                            count={safeDna.totalCorrections + safeDna.totalApprovals}
                            color="var(--accent)" /* Fix 6: was pink */
                            badge={safeDna.totalApprovals > 0 ? `${safeDna.totalApprovals} approved` : undefined}
                        />
                        <ChannelCard
                            icon={<MessageCircle size={20} />}
                            label="Debrief"
                            description="Your reasoning explanations"
                            count={safeDna.totalDebriefs}
                            color="var(--accent)" /* Fix 6: normalised */
                        />
                    </div>
                </div>
            </div>

            {/* Competency Metrics */}
            <div style={styles.metricsSection}>
                <h4 style={styles.sectionTitle}>
                    <Target size={16} color="rgba(255,255,255,0.35)" />
                    Competency Metrics
                </h4>
                <div style={styles.metricsGrid}>
                    <MetricBar icon={<TrendingUp size={14} />} label="Acceptance Rate" value={safeDna.acceptanceRate} color="var(--accent)" suffix="%" />
                    <MetricBar icon={<PenLine size={14} />} label="Correction Rate" value={100 - safeDna.correctionRate} color="var(--accent)" suffix="%" invertLabel />
                    <MetricBar icon={<Sparkles size={14} />} label="Style Match" value={safeDna.styleMatchScore} color="var(--accent)" suffix="%" />
                    <MetricBar icon={<Brain size={14} />} label="Judgment" value={safeDna.judgmentScore} color="var(--accent)" suffix="%" />
                    <MetricBar icon={<Shield size={14} />} label="Escalation Accuracy" value={safeDna.escalationAccuracy} color="var(--accent)" suffix="%" />
                    <MetricBar icon={<BookOpen size={14} />} label="Domain Expertise" value={safeDna.domainExpertise} color="var(--accent)" suffix="%" />
                </div>
            </div>

            {/* Daily Activity */}
            {progress && (
                <div style={styles.activitySection}>
                    <h4 style={styles.sectionTitle}>
                        <Zap size={16} color="rgba(255,255,255,0.35)" />
                        Last 7 Days Activity
                    </h4>
                    <div style={styles.activityGrid}>
                        {progress.dailyActivity.map((day, i) => {
                            const maxCount = Math.max(...progress.dailyActivity.map(d => d.count), 1);
                            const height = Math.max(4, (day.count / maxCount) * 40);
                            const dayLabel = new Date(day.date).toLocaleDateString('en', { weekday: 'short' });
                            return (
                                <div key={i} style={styles.activityDay}>
                                    <div
                                        style={{
                                            ...styles.activityBar,
                                            height,
                                            backgroundColor: day.count > 0 ? 'var(--accent)' : 'rgba(255,255,255,0.06)',
                                            boxShadow: day.count > 0 ? '0 0 8px rgba(0,229,255,0.3)' : 'none',
                                        }}
                                    />
                                    <span style={styles.activityLabel}>{dayLabel}</span>
                                    {day.count > 0 && (
                                        <span style={{ ...styles.activityCount, color: 'var(--accent)' }}>{day.count}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Motivation footer */}
            <div style={styles.footer}>
                <p style={styles.footerText}>
                    The better you train your replica, the better it becomes YOU.
                    Every correction teaches it something new.
                </p>
            </div>
        </div>
    );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function ChannelCard({
    icon,
    label,
    description,
    count,
    color,
    badge,
}: {
    icon: React.ReactNode;
    label: string;
    description: string;
    count: number;
    color: string;
    badge?: string;
}) {
    return (
        <div style={{
            ...styles.channelCard,
            borderColor: `${color}22`,
        }}>
            <div style={{ ...styles.channelIcon, backgroundColor: `${color}15`, color }}>
                {icon}
            </div>
            <div style={styles.channelInfo}>
                <div style={styles.channelHeader}>
                    <span style={styles.channelLabel}>{label}</span>
                    <span style={{ ...styles.channelCount, color }}>{count}</span>
                </div>
                <span style={styles.channelDesc}>{description}</span>
                {badge && (
                    <span style={{ ...styles.channelBadge, backgroundColor: `${color}15`, color }}>
                        {badge}
                    </span>
                )}
            </div>
        </div>
    );
}

function MetricBar({
    icon,
    label,
    value,
    color: _color,
    suffix = '',
    invertLabel = false,
}: {
    icon: React.ReactNode;
    label: string;
    value: number;
    color: string;
    suffix?: string;
    invertLabel?: boolean;
}) {
    const displayValue = Math.round(value);
    return (
        <div style={styles.metricRow}>
            <div style={styles.metricLabel}>
                <span style={{ color: 'rgba(255,255,255,0.4)', marginRight: 6, display: 'flex' }}>{icon}</span>
                <span>{label}</span>
            </div>
            <div style={{ ...styles.metricBarOuter, height: 4 }}>
                {value > 0 && (
                <div
                    style={{
                        ...styles.metricBarInner,
                        height: 4,
                        width: `${Math.min(100, value)}%`,
                        backgroundcolor: 'var(--accent)',
                        boxShadow: 'none',
                        transition: 'width 0.4s ease',
                    }}
                />
                )}
            </div>
            <span style={{ ...styles.metricValue, color: value > 0 ? 'var(--accent)' : 'rgba(255,255,255,0.35)', fontFamily: "'DM Mono', monospace", fontSize: 11 }}>
                {invertLabel ? `${100 - displayValue}${suffix}` : `${displayValue}${suffix}`}
            </span>
        </div>
    );
}

// ============================================
// HELPERS
// ============================================

function getScoreColor(score: number): string {
    if (score >= 80) return '#34d399'; // Emerald
    if (score >= 60) return '#a78bfa'; // Purple
    if (score >= 40) return '#fbbf24'; // Amber
    if (score >= 20) return '#f97316'; // Orange
    return '#ef4444'; // Red
}

function getScoreDescription(score: number): string {
    if (score >= 85) return 'Your twin is deeply aligned with your expertise';
    if (score >= 70) return 'Strong understanding — keep refining with debriefs';
    if (score >= 50) return 'Good progress — corrections are making a difference';
    if (score >= 30) return 'Learning fast — every session counts';
    if (score >= 10) return 'Early days — shadow observations building the foundation';
    return 'Just getting started — train daily to accelerate learning';
}

// ============================================
// STYLES
// ============================================

const styles: Record<string, React.CSSProperties> = {
    container: {
        background: '#111827',
        borderRadius: 12,
        border: '0.5px solid rgba(255,255,255,0.07)',
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    loadingPulse: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 0',
        color: 'rgba(255,255,255,0.4)',
        fontSize: 13,
    },
    ownershipBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        borderRadius: 10,
        background: 'rgba(167,139,250,0.05)',
        border: '1px solid rgba(167,139,250,0.12)',
        fontSize: 12,
        color: 'rgba(255,255,255,0.7)',
        lineHeight: 1.5,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    title: {
        fontSize: 18,
        fontWeight: 700,
        color: '#fff',
        margin: 0,
    },
    subtitle: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.5)',
        margin: 0,
    },
    headerRight: {},
    timeSavedBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 20,
        background: 'rgba(0,229,255,0.1)',
        border: '1px solid rgba(0,229,255,0.2)',
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--accent)',
    },
    mainGrid: {
        display: 'grid',
        gridTemplateColumns: '180px 1fr',
        gap: 20,
        alignItems: 'start',
    },
    scoreSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
    },
    scoreRing: {
        position: 'relative' as const,
        width: 140,
        height: 140,
    },
    scoreCenter: {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    scoreBig: {
        fontSize: 32,
        fontWeight: 800,
        lineHeight: 1,
    },
    scoreLabel: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.4)',
        marginTop: 2,
        textTransform: 'uppercase' as const,
        letterSpacing: 1,
    },
    scoreDescription: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.5)',
        textAlign: 'center' as const,
        margin: 0,
        lineHeight: 1.4,
    },
    scoreText: {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontWeight: 700,
    },
    channelsSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 10,
        fontWeight: 700,
        color: 'rgba(255,255,255,0.35)',
        margin: 0,
        marginBottom: 12,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.1em',
    },
    channelGrid: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    channelCard: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        borderRadius: 10,
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
    },
    channelIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: 8,
        flexShrink: 0,
    },
    channelInfo: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    channelHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    channelLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: '#fff',
    },
    channelCount: {
        fontSize: 16,
        fontWeight: 700,
    },
    channelDesc: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.4)',
    },
    channelBadge: {
        fontSize: 10,
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: 4,
        marginTop: 2,
        display: 'inline-block',
        width: 'fit-content',
    },
    metricsSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    metricsGrid: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    metricRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
    metricLabel: {
        display: 'flex',
        alignItems: 'center',
        fontSize: 12,
        color: 'rgba(255,255,255,0.6)',
        width: 160,
        flexShrink: 0,
    },
    metricBarOuter: {
        flex: 1,
        height: 4,
        borderRadius: 999,
        background: 'rgba(255,255,255,0.06)',
        overflow: 'hidden',
    },
    metricBarInner: {
        height: 4,
        borderRadius: 999,
        transition: 'width 0.4s ease',
    },
    metricValue: {
        fontSize: 13,
        fontWeight: 700,
        width: 44,
        textAlign: 'right' as const,
    },
    activitySection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    activityGrid: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        height: 60,
        gap: 6,
    },
    activityDay: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        flex: 1,
    },
    activityBar: {
        width: '100%',
        maxWidth: 20,
        borderRadius: 3,
        transition: 'height 0.5s ease',
    },
    activityLabel: {
        fontSize: 10,
        color: 'rgba(255,255,255,0.3)',
    },
    activityCount: {
        fontSize: 10,
        fontWeight: 600,
        color: 'var(--accent)',
    },
    footer: {
        borderTop: '1px solid rgba(255,255,255,0.06)',
        paddingTop: 14,
    },
    footerText: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.35)',
        textAlign: 'center' as const,
        margin: 0,
        fontStyle: 'italic',
    },
    // Compact styles
    compactContainer: {
        background: '#111827',
        borderRadius: 12,
        border: '0.5px solid rgba(255,255,255,0.07)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
    },
    compactHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    compactTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: '#fff',
    },
    compactBody: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
    },
    scoreRingCompact: {
        position: 'relative' as const,
        width: 60,
        height: 60,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    compactStats: {
        display: 'flex',
        gap: 16,
        flex: 1,
    },
    compactStat: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    compactStatValue: {
        fontSize: 16,
        fontWeight: 700,
        color: '#fff',
    },
    compactStatLabel: {
        fontSize: 10,
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase' as const,
        letterSpacing: 0.5,
    },
    // Compact learning channels (Shadow → Correct → Debrief)
    compactChannels: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingTop: 10,
        borderTop: '1px solid rgba(255,255,255,0.06)',
    },
    compactChannel: {
        display: 'flex',
        alignItems: 'center',
        gap: 5,
    },
    compactChannelCount: {
        fontSize: 13,
        fontWeight: 700,
    },
    compactChannelLabel: {
        fontSize: 10,
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase' as const,
        letterSpacing: 0.3,
    },
    compactChannelArrow: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.2)',
        fontWeight: 600,
    },
};
